import React, {useState} from 'react';
import {List, useListContext, useTranslate, usePermissions} from 'react-admin';
import {Box, Pagination, Typography} from '@mui/material';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import SavedViewsTabs from '../components/statamic/SavedViewsTabs';
import FilterBar from '../components/statamic/FilterBar';
import ScopeBanner from '../components/statamic/ScopeBanner';
import useScopedFilter from '../scope/useScopedFilter';
import EmptyState from '../components/statamic/EmptyState';
import {SkeletonCard} from '../components/statamic/Skeleton';
import PostCard from './PostCard';


const StatamicPagination = ({translate}) => {
    const {page, perPage, total, setPage} = useListContext();
    const pageCount = Math.max(1, Math.ceil((total ?? 0) / (perPage ?? 25)));
    if (pageCount <= 1) return null;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                py: 1.5,
                borderTop: `1px solid ${t.color.border.hairline}`,
            }}
        >
            <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.muted}}>
                {translate('Showing page %{page} of %{pageCount} · %{total} total', {page, pageCount, total: total ?? 0})}
            </Typography>
            <Pagination
                count={pageCount}
                page={page}
                onChange={(_, p) => setPage(p)}
                size="small"
                shape="rounded"
                sx={{
                    '& .MuiPaginationItem-root': {
                        fontSize: `${t.type.caption.size}px`,
                        color: t.color.text.secondary,
                        borderColor: t.color.border.default,
                    },
                    '& .Mui-selected': {
                        backgroundColor: `${t.color.accent.primaryTint} !important`,
                        color: `${t.color.accent.primary} !important`,
                    },
                }}
            />
        </Box>
    );
};

const PostListStatamicInner = () => {
    const {data, isLoading, filterValues, setFilters, displayedFilters} = useListContext();
    const translate = useTranslate();
    const {permissions} = usePermissions();
    const [activeViewId, setActiveViewId] = useState('all');
    useScopedFilter(true);

    const canSeeStatus = permissions === 'admin' || permissions === 'umbrella';

    const views = [
        {id: 'all',       label: translate('All')},
        {id: 'pending',   label: translate('Pending')},
        {id: 'published', label: translate('Published')},
        {id: 'rejected',  label: translate('Rejected')},
    ];

    const handleViewSelect = (id) => {
        setActiveViewId(id);
        const base = {...filterValues};
        delete base.status;
        if (id === 'pending')        setFilters({...base, status: 'PENDING_APPROVAL'}, displayedFilters);
        else if (id === 'published') setFilters({...base, status: 'PUBLISHED'}, displayedFilters);
        else if (id === 'rejected')  setFilters({...base, status: 'REJECTED'}, displayedFilters);
        else                         setFilters(base, displayedFilters);
    };

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <PageHeader
                title={translate('Posts')}
                subtitle={translate('Updates shared by volunteers and coordinators')}
            />

            <Box
                sx={{
                    backgroundColor: t.color.surface,
                    border: `1px solid ${t.color.border.default}`,
                    borderRadius: `${t.radius.md}px`,
                    mx: 3,
                    mb: 3,
                    overflow: 'hidden',
                }}
            >
                <ScopeBanner/>
                {canSeeStatus && (
                    <SavedViewsTabs
                        views={views}
                        activeId={activeViewId}
                        onSelect={handleViewSelect}
                        onAdd={() => {}}
                    />
                )}

                <FilterBar
                    searchValue={filterValues?.q}
                    onSearchChange={(q) => setFilters({...filterValues, q}, displayedFilters)}
                    placeholder={translate('Search posts')}
                />

                <Box sx={{p: 2}}>
                    {isLoading && (
                        <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 2}}>
                            {Array.from({length: 6}).map((_, i) => <SkeletonCard key={i}/>)}
                        </Box>
                    )}
                    {!isLoading && data?.length === 0 && (
                        <EmptyState
                            title={translate('No posts yet')}
                            description={translate('Posts will appear here once volunteers share updates about their sponsored children.')}
                        />
                    )}
                    {!isLoading && data?.length > 0 && (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                                gap: 2,
                            }}
                        >
                            {data.map(post => (
                                <PostCard
                                    key={post.id}
                                    post={post}
                                    showStatus={canSeeStatus}
                                />
                            ))}
                        </Box>
                    )}
                </Box>

                <StatamicPagination translate={translate}/>
            </Box>
        </Box>
    );
};

const PostListStatamic = (props) => (
    <List
        {...props}
        sort={{field: 'id', order: 'DESC'}}
        perPage={25}
        actions={false}
        pagination={false}
        component={Box}
        sx={{
            backgroundColor: t.color.page,
            '& .RaList-main': {backgroundColor: t.color.page},
        }}
    >
        <PostListStatamicInner/>
    </List>
);

export default PostListStatamic;
